import React from 'react'
import { type SurveyCompletionRate } from '../../models/Division'
import { SectionTableFeature } from './components/SectionTable'
import { TopLevelSurveyResult } from './components/TopLevelSurveyResult'
import SurveyReportItems from './components/SurveyReportItems'
import { type ICalculation, type KeyStatSurveyItem, type ReportsV2 } from '../../models/Report'
import { SurveyScoreChart } from './components/SurveyScoreChart'
import { ReportsActionType, useReportsContext } from './ReportsContext'
import EmptyListCard from '../../features/InstructorDashboardFeature/components/EmptyListCard'
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material'
import { grey } from '../../colors'
import ReportInsightsSkeleton from './components/SurveyReportItems/ReportInsightsSkeleton'
import {
  createFallbackReportInsightsLoadingSections,
  type ReportInsightsLoadingSection
} from './components/reportInsightsLoading'
import { type SurveyResponseMenuSelection } from './components/Charts/BarChart'
import { ACCOUNT_OWNER_REPORT_NOT_FOUND_MESSAGE, areAllSectionsExpanded, buildSectionExpansionMap, formatReportSurfaceErrorMessage, getResponseFilterQuestionOptions, isAccountOwnerReportNotFound, isInsufficientResponsesError, isNoReportAccessError, shouldShowNoResultsAvailable, shouldShowSurveyScoreChart } from './components/helper'
import ResultsAccessCard from './components/ResultsAccessCard'
import { ROLES } from '../../consts/roles'

export interface ReportsFeatureProps {
  completionRate: SurveyCompletionRate | null
  reports: ReportsV2
  originalReports?: ReportsV2 | null
  reportError: Record<string, any> | null
  /** The caller's active role name — rendered in the Results Access Card's meta row. */
  activeRoleName?: string | null
  parentSurveyScore?: number | null
  isReportsPending?: boolean
  isCompletionRateError?: boolean
  seriesId?: string | null
  termId?: string | null
  pendingTitles?: string[] | null
  reportLoadingSections?: ReportInsightsLoadingSection[]
  setSelectedTab?: React.Dispatch<React.SetStateAction<string>>
  onFilterBySurveyResponse?: (selection: SurveyResponseMenuSelection) => void | Promise<void>
  responseFilterOverrideSurveyItem?: KeyStatSurveyItem | null
  responseFilterHighlightedAnswers?: string[]
  responseFilterExistingCalculations?: ICalculation[] | null
  isResponseFilterApplied?: boolean
  responseFilterWarningMessage?: string | null
  onRemoveResponseFilterAnswer?: (answer: string) => void | Promise<void>
  onOpenCommentsExport?: () => void
  onRequestCommentsExport?: (questionScope: { surveyItemId: string, revisionId: number }) => void
  disableFlaggedCommentActions?: boolean
  fetchSectionTableData?: (params: any) => Promise<any>
  /** categoryName of the objectId division, derived from topBarDivisions cache so SectionTable skips its own completion-rate fetch. */
  objectCategoryName?: string
}

const ReportsFeature: React.FC<ReportsFeatureProps> = (props) => {
  const {
    completionRate,
    reports,
    originalReports = null,
    reportError,
    activeRoleName,
    parentSurveyScore,
    isReportsPending = false,
    isCompletionRateError = false,
    seriesId,
    termId,
    pendingTitles = null,
    reportLoadingSections = createFallbackReportInsightsLoadingSections(),
    setSelectedTab,
    onFilterBySurveyResponse,
    responseFilterOverrideSurveyItem = null,
    responseFilterHighlightedAnswers = [],
    responseFilterExistingCalculations = null,
    isResponseFilterApplied = false,
    responseFilterWarningMessage = null,
    onRemoveResponseFilterAnswer,
    onOpenCommentsExport,
    onRequestCommentsExport,
    disableFlaggedCommentActions = false,
    fetchSectionTableData,
    objectCategoryName
  } = props
  const showSectionTable = completionRate?.categoryName && String(completionRate.categoryName) !== 'Section'
  const { selectedTrend, dispatch } = useReportsContext()
  const showSurveyScoreChart = shouldShowSurveyScoreChart(selectedTrend, reports)
  const showNoResultsAvailable = !isReportsPending && !reportError && shouldShowNoResultsAvailable(reports, completionRate)
  const disableTopLevelControls = isReportsPending || !!reportError || showNoResultsAvailable || !(reports?.surveySections?.length)
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [expandAllRequestId, setExpandAllRequestId] = React.useState(0)
  const [expandedSections, setExpandedSections] = React.useState<Record<number, boolean>>({})
  const reportSignature = React.useMemo(() => JSON.stringify({
    titles: reports?.titles ?? [],
    surveySections: reports?.surveySections?.map((section) => section.title ?? '').join('|') ?? '',
    selectedTrend
  }), [reports?.titles, reports?.surveySections, selectedTrend])

  React.useEffect(() => {
    dispatch({ type: ReportsActionType.RESET_PRESENTATION_STATE })
  }, [dispatch, reportSignature])

  React.useEffect(() => {
    const sectionCount = reports?.surveySections?.length ?? 0

    setExpandedSections((prev) => {
      if (sectionCount === 0) {
        return prev
      }

      return Array.from({ length: sectionCount }).reduce<Record<number, boolean>>((acc, _, idx) => {
        acc[idx] = prev[idx] ?? false
        return acc
      }, {})
    })
  }, [reports?.surveySections])

  React.useEffect(() => {
    const sectionCount = reports?.surveySections?.length ?? 0
    if (sectionCount === 0) {
      return
    }

    const shouldShowExpandedAtTopLevel = areAllSectionsExpanded(expandedSections, sectionCount)
    setIsExpanded((prev) => (prev === shouldShowExpandedAtTopLevel ? prev : shouldShowExpandedAtTopLevel))
  }, [expandedSections, reports?.surveySections])

  const responseFilterAnswerOrder = React.useMemo(() => {
    if (!responseFilterOverrideSurveyItem?.options?.length) {
      return []
    }

    return responseFilterOverrideSurveyItem.options
      .map((option) => option.name)
      .filter((answer): answer is string => Boolean(answer))
  }, [responseFilterOverrideSurveyItem?.options])
  const responseFilterQuestionOptions = React.useMemo(
    () => getResponseFilterQuestionOptions(originalReports ?? reports),
    [originalReports, reports]
  )

  const handleToggleAllSections = (): void => {
    const nextExpanded = !isExpanded
    setIsExpanded(nextExpanded)
    setExpandedSections(buildSectionExpansionMap(reports?.surveySections?.length ?? 0, nextExpanded))
    setExpandAllRequestId((prev) => prev + 1)
  }

  const renderReports = (isExpanded: boolean, setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>): JSX.Element => {
    if (isReportsPending) {
      return <ReportInsightsSkeleton sections={reportLoadingSections} />
    }
    if (isNoReportAccessError(reportError)) {
      return <ResultsAccessCard reason="permission" roleName={activeRoleName} />
    }
    if (isInsufficientResponsesError(reportError)) {
      return (
        <ResultsAccessCard
          reason="insufficientResponses"
          minResponses={{
            current: reportError?.data?.error?.current,
            min: reportError?.data?.error?.min
          }}
        />
      )
    }
    if (!reports || reportError) {
      const isCollectingData = reportError?.data?.earlyAccess_1ReleaseDate === null && reportError?.data?.earlyAccess_2ReleaseDate === null
      const isAccountOwner = activeRoleName === ROLES.ACCOUNT_OWNER
      const reportSurfaceErrorMessage = isAccountOwnerReportNotFound(reportError, isAccountOwner)
        ? ACCOUNT_OWNER_REPORT_NOT_FOUND_MESSAGE
        : formatReportSurfaceErrorMessage(reportError?.data ?? reportError?.message)
      return (
        <Card>
          <Box sx={{ p: `${theme.spacing(8)} ${theme.spacing(6)}` }}>
            {isCollectingData
              ? <Typography color={grey[850]} >
                Results for this survey are currently being collected.
              </Typography>
              : <Typography component={'span'} fontWeight={500} color={grey[250]}>
                {reportSurfaceErrorMessage}
              </Typography>
            }
          </Box>
        </Card>
      )
    }
    if (reports?.surveySections?.length && !reportError && completionRate && seriesId && termId) {
      return (
        <SurveyReportItems
          reports={reports}
          originalReports={originalReports}
          completionRate={completionRate}
          reportError={reportError}
          expandedSections={expandedSections}
          onExpandedSectionsChange={setExpandedSections}
          onFilterBySurveyResponse={onFilterBySurveyResponse}
          onRemoveResponseFilterAnswer={onRemoveResponseFilterAnswer}
          responseFilterOverrideSurveyItem={responseFilterOverrideSurveyItem}
          responseFilterHighlightedAnswers={responseFilterHighlightedAnswers}
          isResponseFilterApplied={isResponseFilterApplied}
          responseFilterWarningMessage={responseFilterWarningMessage}
          onRequestCommentsExport={onRequestCommentsExport}
          disableFlaggedCommentActions={disableFlaggedCommentActions}
        />
      )
    }
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {/* TODO: Display an empty state component (e.g., <EmptyListCard />) if no survey reports are available */}
        {/* <EmptyListCard /> */}
      </Box>
    )
  }

  const renderNoProjectOrTerm = (): JSX.Element => (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%" >
      <Card sx={{ p: 4 }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            To see Reports, select a Survey Project and Term.
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Use the dropdowns above to choose a project and term to view results.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )

  const renderNoResultsAvailable = (): JSX.Element => (
    <Card>
      <Box sx={{ p: `${theme.spacing(8)} ${theme.spacing(6)}` }}>
        <Typography color={grey[850]}>
          No results available.
        </Typography>
      </Box>
    </Card>
  )

  const renderMainReport = (): JSX.Element => (
    <React.Fragment>
      {showSectionTable && !showNoResultsAvailable && <SectionTableFeature completionRate={completionRate} parentSurveyScore={parentSurveyScore} setSelectTab={setSelectedTab} fetchSectionTableData={fetchSectionTableData} objectCategoryName={objectCategoryName} />}
      <TopLevelSurveyResult
        calculations={showNoResultsAvailable ? [] : reports?.calculations}
        comparisonCalculations={showNoResultsAvailable ? null : responseFilterExistingCalculations}
        titles={isReportsPending && pendingTitles?.length ? pendingTitles : reports?.titles}
        onToggleExpanded={handleToggleAllSections}
        isExpanded={isExpanded}
        disableControls={disableTopLevelControls}
        onRemoveResponseFilterAnswer={onRemoveResponseFilterAnswer}
        responseFilterAnswerOrder={responseFilterAnswerOrder}
        responseFilterQuestionOptions={responseFilterQuestionOptions}
        responseFilterWarningMessage={responseFilterWarningMessage}
        onOpenCommentsExport={onOpenCommentsExport}
        isSectionLevel={!showSectionTable}
        modifiedOn={reports?.modifiedOn ?? completionRate?.modifiedOn ?? null}
      />
      {showSurveyScoreChart && <SurveyScoreChart reports={reports} />}
      {showNoResultsAvailable ? renderNoResultsAvailable() : renderReports(isExpanded, setIsExpanded)}
    </React.Fragment>
  )

  if (!seriesId || !termId) {
    return renderNoProjectOrTerm()
  }

  // Instructor report view skips the page-level loader, so the first paints
  // (and project/term switches) arrive here with completionRate still null.
  // Keep the insights skeleton up until that gate resolves — do not flash
  // "No results found" for an in-flight request.
  // Spec: docs/specs/active/bugfix-instructor-report-empty-flash/spec.md
  if (isReportsPending && !isCompletionRateError) {
    return renderMainReport()
  }

  if (completionRate && !isCompletionRateError) {
    return renderMainReport()
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <EmptyListCard />
    </Box>
  )
}

export default ReportsFeature
